import { AdjustmentFilter } from 'pixi-filters'
import * as PIXI from 'pixi.js-legacy'
import eventsObservable from '../../../framework/src/events/EventsObservable'
import { wait } from '../../../framework/src/helpers'
import {
	EASE,
	getTween,
	getTweenAsync,
} from '../../../framework/src/modules/Tween'
import { JokerWinWrapper } from './JokerWinWrapper'
import { textStyle } from './constants/constants'
import { JokerBonusHelpers } from './helpers'
import { IColorScheme } from './interfaces/IColorScheme'
import { IJackpotNotification } from './interfaces/IJackpotWin'
export class JokerWinCategory extends PIXI.Container {
	digits: PIXI.Sprite[]
	parent: JokerWinWrapper

	protected lastValue: number
	public borderWidth = 10 // 2% of height
	protected padding = 25
	protected digitsCount: number
	pixiCurrencyText: PIXI.Text
	decimal: PIXI.Text
	icon: PIXI.Sprite
	bg: PIXI.Sprite
	bg2: PIXI.Sprite
	lineHeight: number
	protected touchCount = 0
	protected ubuntuMonoSideRatio = 0.55
	fontSize: number
	fontHeight: number
	private _text: PIXI.Text
	private animationPromise: Promise<void[]>
	listeningForOtherCategoriesPause: boolean
	resumeTimeout: number
	minDigitsCount: number = 6 + 2
	minWidth: number
	brihtnessFilter = new AdjustmentFilter()
	resetPromise: Promise<void>
	resetPromiseCB: (value: void | PromiseLike<void>) => void
	isBlinking: any
	protected decimalPlaces: number

	constructor(
		public categoryName: string,
		_width: number,
		_height: number,
		protected currencyText: string,
		public colorScheme: IColorScheme,
		initValue: number = 0,
		digitsCount: number = 0,
		protected readonly animLength: number,
		protected readonly _decimalPlaces: number
	) {
		super()
		this.decimalPlaces =
			this._decimalPlaces > 0 && initValue >= 10 ** this._decimalPlaces ? 2 : _decimalPlaces
		this.filters = [this.brihtnessFilter]
		const preciseBorderWidth = _height * 0.1
		this.borderWidth = Math.max(
			preciseBorderWidth % 2 > 1
				? Math.ceil(preciseBorderWidth)
				: Math.floor(preciseBorderWidth),
			2
		)
		this.padding = _height * 0.2
		this.lineHeight = _height
		this.createIcon(_height)
		this.digitsCount = Math.max(digitsCount, this.minDigitsCount)
		this.minWidth = _width
		this.createTexts(_height)
		this.createBackground(_height, _width)

		this.bg2.addChild(this.pixiCurrencyText)
		this.pixiCurrencyText.position.set(
			this.bg2.width - this.padding / 2,
			this.bg2.height / 2
		)
		this.bg2.addChild(this.icon)
		this.bg2.addChild(this.decimal)
		this.digits.forEach((digit) => {
			this.bg2.addChild(digit)
		})
		this.setXPositions(
			this.decimalPlaces !== this._decimalPlaces
				? Math.floor(initValue / 10 ** (this._decimalPlaces - 2))
				: initValue
		)

		const mask = PIXI.Sprite.from(
			JokerBonusHelpers.solidFillTexture(
				this.bg2.width,
				this.bg2.height,
				'#ffffff'
			)
		)
		this.bg2.mask = mask
		mask.isSprite = false
		this.bg2.addChild(mask)

		this.interactive = true
		//@ts-ignore
		this.on('pointerover', () => {
			eventsObservable.dispatch('joker_bonus-pause', this)
		})
		//@ts-ignore
		this.on('pointerdown', () => {
			if (++this.touchCount % 2 === 1) {
				this.firePauseEvent()
			} else {
				this.fireResumeEvent()
			}
		})
		//@ts-ignore
		this.on('pointerout', () => {
			if (this.touchCount === 0) {
				eventsObservable.dispatch('joker_bonus-resume', this)
			}
		})
		this.setResetPromise()
	}
	private setXPositions(initValue: number) {
		this.digits.forEach((text, index, digits) => {
			text.x =
				digits[index - 1]?.x - text.width ||
				this.pixiCurrencyText.x - this.pixiCurrencyText.width * 2
			if (index != 0 && index % 3 === this.decimalPlaces % 3) {
				text.x -= text.width / 2
			}
		})
		const decimalIndex = Math.max(this.decimalPlaces - 1, 0)
		this.decimal.visible = this.decimalPlaces > 0
		this.decimal.x =
			this.digits[decimalIndex].x - this.decimal.width * 0.75
		this.setDigitsInitialPositions(initValue)
	}

	setResetPromise() {
		this.resetPromise = new Promise<void>((res) => {
			this.resetPromiseCB = res
		})
	}

	onOtherCategoryPause = (name: string, data: JokerWinCategory) => {
		if (
			data?.groupId === this.groupId &&
			data?.categoryName === this.categoryName
		) {
			//this event was dispatched by this category, ignore it.
			return
		}
		this.touchCount = 0
	}

	private fireResumeEvent() {
		eventsObservable.dispatch('joker_bonus-resume', this)
		this.listeningForOtherCategoriesPause = false
		this.touchCount = 0
		eventsObservable.unsubscribe('joker_bonus-pause', this.onOtherCategoryPause)
		clearTimeout(this.resumeTimeout)
	}

	private firePauseEvent() {
		eventsObservable.dispatch('joker_bonus-pause', this)
		this.listeningForOtherCategoriesPause = true
		eventsObservable.subscribe('joker_bonus-pause', this.onOtherCategoryPause)
		this.resumeTimeout = setTimeout(() => {
			this.fireResumeEvent()
		}, 5000) as unknown as number
	}

	get groupId() {
		return this.parent?.groupId
	}

	private createBackground(height: number, minWidth: number) {
		const bgWidth = Math.max(
			this.icon.width +
				this.digits.reduce((prev, current, index) => {
					return prev + (index % 3 === 2 ? current.width * 2 : current.width)
				}, 0) +
				this.pixiCurrencyText.width +
				this.padding,
			minWidth
		)
		this.bg = PIXI.Sprite.from(
			JokerBonusHelpers.linearGradientTexture(
				bgWidth,
				height,
				90,
				this.colorScheme.borderColors,
				this.colorScheme.borderColorStops
			)
		)
		this.bg2 = PIXI.Sprite.from(
			JokerBonusHelpers.linearGradientTexture(
				bgWidth - this.borderWidth,
				height - this.borderWidth,
				90,
				this.colorScheme.backgroundColors,
				this.colorScheme.backgroundColorStops
			)
		)
		this.bg2.x = this.borderWidth / 2
		this.bg2.y = this.borderWidth / 2

		this.bg.addChild(this.bg2)

		this.addChild(this.bg)
	}

	private setDigitsInitialPositions(initValue: number) {
		this.lastValue = initValue

		let stringNew = initValue.toString().padStart(this.decimalPlaces + 1, '0')
		const paddedNew = stringNew.padStart(this.digitsCount, '0')
		for (let index = 0; index < this.digitsCount - stringNew.length; index++) {
			this.digits[this.digitsCount - 1 - index].y = 0
		}
		for (
			let index = this.digitsCount - stringNew.length;
			index < this.digitsCount;
			index++
		) {
			const digitIndex = this.digitsCount - 1 - index
			this.digits[digitIndex].y =
				(parseInt(paddedNew[index]) + 1) * this.lineHeight * -1 +
				this.borderWidth / 2 +
				this.lineHeight / 2 -
				this.fontSize / 2
		}
	}

	private createIcon(height: number) {
		this.icon = PIXI.Sprite.from(
			`${JokerBonusHelpers.getConfigUrl()}/${this.colorScheme.iconUrl}`
		)
		if (this.colorScheme.iconTint) {
			this.icon.tint = parseInt(this.colorScheme.iconTint.replace('#', ''), 16)
		}
		this.icon.anchor.set(0, 0.5)
		this.icon.height = height - this.borderWidth
		this.icon.y = this.icon.height / 2
		this.icon.scale.x = this.icon.scale.y
		this.icon.x = this.padding / 2
	}

	destroy(options?: boolean | PIXI.IDestroyOptions): void {
		this.resetPromiseCB?.()
		this.setResetPromise()
		this.digits.forEach((digit) => {
			createjs.Tween.removeTweens(digit)
		})
		if (this.listeningForOtherCategoriesPause) {
			eventsObservable.unsubscribe(
				'joker_bonus-pause',
				this.onOtherCategoryPause
			)
		}
		if (this.resumeTimeout) {
			clearTimeout(this.resumeTimeout)
		}
		// //@ts-ignore
		// this.off("mouseover");
		// //@ts-ignore
		// this.off("touchstart");
		// //@ts-ignore
		// this.off("mouseout");
		// //@ts-ignore
		// this.off("touchend");
		// //@ts-ignore
		// this.off("touchendoutside");
		//@ts-ignore
		this.off('pointerdown')
		//@ts-ignore
		this.off('pointerover')
		//@ts-ignore
		this.off('pointerdown')

		this.animationPromise = null

		this._text.destroy(true)
		this.decimal.destroy(true)
		this.pixiCurrencyText.destroy(true)
		this.icon.destroy()
		this.bg.destroy(true)
		// this.digits.forEach((digit) => digit.destroy(true));
		super.destroy(options)
	}

	private createTexts(height: number) {
		this.fontSize = Math.floor(height - this.borderWidth)
		let defaultTextStyle = this.getDefaultTextStyle(height)
		this.createCurrencyLabel(defaultTextStyle, height - this.borderWidth * 2)

		this.recalculateDigitsSize(defaultTextStyle)
		this.createDigits(defaultTextStyle)
		this.createDigitSeparators(defaultTextStyle)
	}

	private getDefaultTextStyle(height: number): Partial<PIXI.ITextStyle> {
		return {
			...textStyle,
			fontSize: this.fontSize,
			fontFamily: 'UbuntuMono-Regular',
			align: 'right',
			lineHeight: height,
			fill: this.colorScheme.textColors,
			fillGradientStops:
				this.colorScheme.textColorStops.map((stop) =>
					parseFloat((stop / 100).toFixed(4))
				) ?? textStyle.fillGradientStops,
		}
	}

	private recalculateDigitsSize(
		defaultTextStyle: Partial<PIXI.ITextStyle>
	): Partial<PIXI.ITextStyle> {
		for (let i = 1; i <= (defaultTextStyle.fontSize as number); i++) {
			const testChar = new PIXI.Text('O', {
				...defaultTextStyle,
				fontSize: i,
				lineHeight: 0,
			})
			this.fontHeight = testChar.height
			const digitsWidth =
				(this.digitsCount + Math.floor(this.digitsCount / 3)) * testChar.width
			testChar.destroy(true)
			if (
				digitsWidth >
				this.minWidth -
					this.icon.width -
					this.pixiCurrencyText.width -
					this.padding -
					this.borderWidth * 2
			) {
				this.fontSize = Math.max(1, i - 1)
				const tmpText = new PIXI.Text('O', {
					...defaultTextStyle,
					lineHeight: 0,
					fontSize: this.fontSize,
				})
				this.fontHeight = tmpText.height
				tmpText.destroy(true)
				defaultTextStyle.fontSize = this.fontSize
				break
			}
		}
		return defaultTextStyle
	}

	private createDigitSeparators(defaultTextStyle: Partial<PIXI.ITextStyle>) {
		this.decimal = new PIXI.Text('.', defaultTextStyle)
		this.decimal.anchor.set(1, 0)

		const decimalIndex = Math.max(this.decimalPlaces - 1, 0)
		this.decimal.visible = this.decimalPlaces > 0

		this.decimal.position.set(
			this.digits[decimalIndex].x +
				this.digits[decimalIndex].width,
			this.borderWidth / 2 + this.lineHeight / 2 - this.fontSize / 2
		)
	}

	private createDigits(defaultTextStyle: Partial<PIXI.ITextStyle>) {
		this._text = new PIXI.Text(
			' \nO\n1\n2\n3\n4\n5\n6\n7\n8\n9\nO',
			defaultTextStyle
		)
		this._text.updateText(false)

		const textTexture = this._text.texture
		this.digits = new Array(this.digitsCount).fill(0).map(() => {
			const res = PIXI.Sprite.from(textTexture)
			res.y = this.padding / 2
			res.anchor.set(1, 0)

			return res
		})
	}

	private createCurrencyLabel(
		defaultTextStyle: Partial<PIXI.ITextStyle>,
		height
	) {
		this.pixiCurrencyText = new PIXI.Text(
			this.currencyText.split('').join('\n'),
			{
				...defaultTextStyle,
				strokeThickness: 0,
				dropShadow: false,
				fill: [defaultTextStyle.fill[1]],
				fontSize: 16 * 1.3,
				lineHeight: 16,
			}
		)
		this.pixiCurrencyText.height = height
		this.pixiCurrencyText.scale.x = this.pixiCurrencyText.scale.y
		this.pixiCurrencyText.anchor.set(1, 0.5)
	}

	private removeDigitsTweens() {
		for (const digit of this.digits) {
			createjs.Tween.removeTweens(digit)
		}
	}

	async blink(data: IJackpotNotification) {
		this.isBlinking = true
		const blinkLength = 175
		const blinkCount = 20
		const maxBrightness = 3

		this.removeDigitsTweens()
		this.rotateDigitsTo(Math.round(data.credit_value))

		for (let i = 0; i < blinkCount; i++) {
			await getTweenAsync(
				this.brihtnessFilter,
				{ brightness: maxBrightness },
				blinkLength
			)
			if (this.destroyed) return
			await getTweenAsync(this.brihtnessFilter, { brightness: 1 }, blinkLength)
			if (this.destroyed) return
		}
		this.resetPromiseCB()
		this.setResetPromise()
		this.isBlinking = false
	}
	rotateDigitsTo(val: number, len: number = 300) {
		const promises: Promise<void>[] = []
		let stringNew = val.toString().padStart(this.decimalPlaces + 1, '0')
		const paddedNew = stringNew.padStart(this.digitsCount, '0')
		for (let i = -1; i < this.digitsCount - stringNew.length; i++) {
			const digit = this.digits[this.digitsCount - 1 - i]
			if (!digit) continue
			promises.push(
				new Promise<void>((res) => {
					getTween(
						digit,
						{
							y: this.getValueY(-1),
						},
						len,
						EASE.LINEAR,
						res
					)
				})
			)
		}
		for (
			let i = this.digitsCount - stringNew.length;
			i < this.digitsCount;
			i++
		) {
			const digit = this.digits[this.digitsCount - 1 - i]
			if (!digit) continue
			const newDigit = parseInt(paddedNew[i])
			promises.push(
				new Promise<void>((res) => {
					getTween(
						digit,
						{
							y: this.getValueY(newDigit),
						},
						len,
						EASE.LINEAR,
						res
					)
				})
			)
		}
		return Promise.all(promises)
	}

	async setNewValue2(_newVal: number) {
		let newVal = _newVal
		this.removeDigitsTweens()
		this.resetPromiseCB()
		this.setResetPromise()
		const minRotDur = 85
		const maxRotDur = 300
		const threshold = 10 ** this._decimalPlaces
		// const rotDur = 150;
		const preciseDigits = this.digits.map(
			(el) =>
				(el.y -
					this.borderWidth / 2 -
					this.lineHeight / 2 +
					this.fontSize / 2) /
					(this.lineHeight * -1) -
				1
		)
		const currentDigits = preciseDigits.map((el) => Math.ceil(el))
		// const currentDigits2=currentDigits.map(el=>Math.round(el))
		const currentVal = currentDigits
			.map((el) => {
				if (el === -1) return 0
				if (el === 10) return 0
				return el
			})
			.reduce((prev, next, i) => prev + next * 10 ** i, 0)
		const lastDecPlaces = this.decimalPlaces
		this.decimalPlaces = this._decimalPlaces > 0 && newVal >= threshold ? 2 : this._decimalPlaces
		this.setXPositions(currentVal)

		if (this.decimalPlaces !== this._decimalPlaces) {
			newVal = Math.floor(newVal / 10 ** (this._decimalPlaces - 2))
		}
		const decreasing =
			currentVal > newVal || lastDecPlaces !== this.decimalPlaces
		const diff = Math.abs(currentVal - newVal)

		const theoreticalRotDur = this.animLength / diff
		const rotDur = Math.min(Math.max(minRotDur, theoreticalRotDur), maxRotDur)

		const maxTicks = Math.floor(this.animLength / rotDur)
		await this.rotateDigitsTo(currentVal, 60)

		const tickWait =
			diff > maxTicks ? 0 : (this.animLength - diff * rotDur) / diff

		if (diff === 0) {
			return wait(3000)
		}
		if (decreasing) {
			return this.rotateDigitsTo(newVal).then(() => (this.lastValue = newVal))
		}

		const tickDigit = async (index = 0) => {
			const d = this.digits[index]
			const c = currentDigits[index]
			const t = createjs.Tween.get(d)
			const p = []
			let cb = null
			const pi = new Promise<void>((res) => {
				cb = res
			})
			p.push(pi)

			if (c == 9) {
				p.push(tickDigit(index + 1))
			}
			try {
				if (c === -1) {
					t.to({ y: this.getValueY(c + 2) }, rotDur)
					currentDigits[index] += 1
				} else {
					t.to({ y: this.getValueY(c + 1) }, rotDur)
					currentDigits[index] += 1
				}

				if (currentDigits[index] >= 10) {
					currentDigits[index] = 0
					t.to({ y: this.getValueY(0) })
				}
			} catch (e) {
				console.debug('JB tick digits error', e)
			}

			t.call(cb)
			await Promise.all(p)
		}
		const debugTickFail = (e) => {
			console.debug(`JB ${this.categoryName} tick digits error`, e)
		}

		try {
			for (let i = 0; i < diff; i++) {
				if (tickWait > 0) {
					await wait(tickWait)
				}
				if (this.destroyed) return
				await tickDigit().catch(debugTickFail)
				if (this.destroyed) return
			}
		} catch (e) {
			debugTickFail(e)
		}
	}
	private getValueY(val) {
		return (
			((val % 11) + 1) * this.lineHeight * -1 +
			this.borderWidth / 2 +
			this.lineHeight / 2 -
			this.fontSize / 2
		)
	}
	async setNewValue(val: number) {
		// this.removeDigitsTweens()
		// this.resetPromiseCB()
		// this.setResetPromise()
		if (this.isBlinking) {
			return wait(1000)
		}
		await Promise.race([this.setNewValue2(val), this.resetPromise])
	}
	setDigitsCount(newCount: number) {
		this.bg2.removeChild(this.decimal),
			this.digits.forEach((digit) => {
				this.bg2.removeChild(digit)
			})
		this.digitsCount = Math.max(this.minDigitsCount, newCount)
		this.fontSize = Math.floor(this.lineHeight - this.borderWidth)
		let defaultTextStyle = this.getDefaultTextStyle(this.lineHeight)
		this.recalculateDigitsSize(defaultTextStyle)
		this.createDigits(defaultTextStyle)
		this.createDigitSeparators(defaultTextStyle)
		this.digits.forEach((digit) => {
			this.bg2.addChild(digit)
		})
		this.digits.forEach((text, index, digits) => {
			text.x =
				digits[index - 1]?.x - text.width ||
				this.pixiCurrencyText.x - this.pixiCurrencyText.width * 2
			if (index != 0 && index % 3 === this.decimalPlaces % 3) {
				text.x -= text.width / 2
			}
		})
		this.bg2.addChild(this.decimal)
		this.decimal.x = this.digits[1].x - this.decimal.width * 0.75

		this.setDigitsInitialPositions(this.lastValue)
	}
}
